import MoreIcon from '@/assets/icons/elipses.svg';
import CheckboxButton from '@/components/shared/buttons/CheckboxButton';
import { ILoanNFT } from '@/redux/loans/model';
import { cn } from '@/utils/helper';

import NettyScore from './NettyScore';

interface ILoanItem {
  name: string;
  collectionName?: string;
  imageUrl?: string;
  id?: string | number;
  details: {
    title: string;
    value: string | number;
    symbol?: string;
    className?: string;
  }[];
  handle?: (id: string | number) => Promise<boolean> | void;
  RenderAdditional?: React.ComponentType | (() => React.ReactNode);
  uniqueId: number | string;
  nettyScore?: number | string | null;
  checkedTr?: (tokenId: number) => boolean;
  handleCheckboxChange?: (token: ILoanNFT) => void;
  token?: ILoanNFT;
  MoreWithButton?: React.ReactNode;
  status?: string;
  isUnpaid?: boolean;
}

const LoanItem: React.FC<ILoanItem> = ({ name, status, token, checkedTr, handleCheckboxChange, collectionName, isUnpaid, id, uniqueId, details, imageUrl, handle, RenderAdditional, nettyScore, MoreWithButton }) => {
  const forclosedStatusClass = '!bg-[#FDE8E8] text-[10px]/3 lg:text-xs font-medium !text-maroon';

  const statusStyles = {
    active: { class: '!bg-light-purple !text-[10px]/3 lg:text-xs !text-primary', label: 'open' },
    paid: { class: '!bg-pale-green !text-[10px]/3 lg:text-xs !text-forest-green', label: 'paid' },
    foreclosed: { class: forclosedStatusClass, label: 'defaulted' },
    unpaid: {
      class: forclosedStatusClass,
      label: 'defaulted',
    },
  };

  const statusFront = isUnpaid ? 'unpaid' : status;
  const currentStatus = statusStyles[statusFront as keyof typeof statusStyles];

  return (
    <div
      className='border-b select-none cursor-pointer dark:hover:!bg-char-coal hover:bg-light-purple px-5 lg:px-6 py-5 flex items-center'
      onClick={() => {
        if (handleCheckboxChange && token) {
          handleCheckboxChange(token);
        } else if (handle && uniqueId) {
          handle(uniqueId);
        }
      }}
    >
      <div className='w-full'>
        <div className={cn(checkedTr ? 'justify-start' : 'justify-between', 'flex items-center gap-[10px] !bg-transparent')}>
          {checkedTr && handleCheckboxChange && uniqueId && token && (
            <CheckboxButton
              value={checkedTr(+uniqueId)}
              onSwitch={() => {
                handleCheckboxChange(token);
              }}
            />
          )}

          <div className='flex items-center gap-[14px]'>
            <img src={imageUrl || '/bayc-collectors.png'} className='size-9 rounded object-contain' alt='item image' />
            <div className='flex flex-col !bg-transparent'>
              <p className='text-sm font-semibold'>{collectionName || name}</p>
              {id && <p className='font-medium text-xs'>#{id}</p>}
            </div>
          </div>

          {nettyScore !== undefined && (
            <div className='flex items-center justify-center gap-3 ss:mr-4 mr-0 lg:mr-0 !bg-light-blue-grey dark:!bg-char-coal px-3 py-1 rounded'>
              <p className='text-xs font-semibold font-space-mono'>NettyScore</p>
              <NettyScore nettyScore={nettyScore} width={24} height={24} outerRadius={12} innerRadius={11} />
            </div>
          )}

          {status && <p className={cn('text-[10px]/3 lg:text-xs font-bold justify-center w-[80px] h-[33px] rounded-[59px] font-space-mono flex items-center', currentStatus.class)}>{currentStatus.label}</p>}

          {MoreWithButton && MoreWithButton}
        </div>

        <div className='pt-3 lg:pt-2 flex w-full flex-wrap items-center gap-2 !bg-transparent justify-between'>
          {details.map(({ title, value, symbol }) => (
            <div key={title} className='flex max-ss:w-full max-ss:max-w-24 flex-col !bg-transparent'>
              <p className='font-semibold truncate text-xs'>{title}</p>
              <p className='font-normal truncate text-xs font-space-mono'>
                {value} {symbol}
              </p>
            </div>
          ))}
          {RenderAdditional && (
            <div className='mt-2 !bg-transparent'>
              <RenderAdditional />
            </div>
          )}
        </div>
      </div>
      <MoreIcon className='w-[3px] lg:w-1.5 [&_path]:fill-coal-250 dark:[&_path]:fill-white ml-4 mr-2 lg:mx-4' />
    </div>
  );
};

export default LoanItem;
